import { useQuery } from "@tanstack/react-query";
import { createLogger } from "logging";
import { useApi, useCustomParams, usePermissions } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo } from "react";

import { BoardSettingsContext } from "./BoardSettingsContext";

const logger = createLogger("BoardSettingsProvider");

export const BoardSettingsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { handleHttpError } = usePermissions();
  const { boardApi } = useApi();
  const { boardId } = useCustomParams();

  const queryResult = useQuery({
    queryKey: [`BoardSettings|${boardId}`],
    queryFn: async ({ signal }) => {
      const data = await boardApi.getBoardSettings(boardId, signal);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: !!boardId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const { refetch, error } = queryResult;
  const boardSettings = queryResult.data;
  const isLoading = queryResult.isFetching;
  useEffect(() => {
    handleHttpError(error);
  }, [error, handleHttpError]);

  const reloadBoardSettings = useCallback(() => {
    refetch();
  }, [refetch]);

  const boardIdToUse = useMemo(() => `${boardSettings?.id || ""}`, [boardSettings]);

  const contextValue = useMemo(
    () => ({
      boardId: boardIdToUse,
      boardSettings: isLoading ? undefined : boardSettings,
      isLoading,
      error,
      reloadBoardSettings,
    }),
    [boardIdToUse, boardSettings, isLoading, error, reloadBoardSettings]
  );

  return <BoardSettingsContext.Provider value={contextValue}>{children}</BoardSettingsContext.Provider>;
};
